//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { selectFileQueue } from '@/redux/selectors/fileQueueSelectors';
import { Button, Grid, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { useRef, useState } from 'react';
import RefreshFiles from '../../ActionBar/RefreshFiles';
import FileItemText from './FileItemText';
import FolderIcon from './FolderIcon';
import { FileManagerInventoryFile, FileSource } from '@/redux/slices/slice_types';
import baseAxios from 'axios';
import axios from '@/_utils/axios';
import FileSelectedIcon from './FileItemIcon';
import FileActions from './FileActions';
import RemoveButton from '@/components/_common/IconButtons/RemoveButton';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { useToast } from '@/components/_common/Toast';
import { useAppSelector } from '@/redux/hooks';
import { formatDate } from '@/_utils/date';
import { fileColumns } from '../FolderContent/FolderContent';
import { selectAllFileSources } from '@/redux/selectors/fileManagerSelectors';
import { formatBytes } from '@/_utils/utils';
import { selectCanWriteFileManager } from '@/redux/selectors/authSelectors';

interface FileItemProps {
  item: FileManagerInventoryFile;
  onClick?: any;
  onRemove?: any;
  selected?: any;
}

const FileItem = ({ item, onClick, onRemove, selected }: FileItemProps) => {
  const { info, error } = useToast();
  const fileQueue = useAppSelector(selectFileQueue);
  const [hover, setHover] = useState(false);
  const inFileQueue = selected || !!fileQueue.find((file) => file.key === item.key && file.origin_slug === item.origin_slug);
  const isFolder = item.ptype === 'FOLDER';
  const isMidAction = item.name.toLowerCase().includes('being copied/moved');
  const fileName = isMidAction ? <em>{item.name.replace('(being copied/moved here)', '')}</em> : item.name;
  const isArchived = item.storage_class === 'GLACIER';
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadingFile, setDownloadingFile] = useState<FileManagerInventoryFile | null>(null);
  const cancelDownload = useRef<(() => void) | null>(null);
  const fileSources = useAppSelector(selectAllFileSources);
  const canWriteFileManager = useAppSelector(selectCanWriteFileManager);


  const mouseEnter = () => setHover(true);
  const mouseLeave = () => setHover(false);

  const handleDownload = (file: FileManagerInventoryFile) => {
    const CancelToken = baseAxios.CancelToken;
    const source = CancelToken.source();

    setDownloadingFile(file);
    setDownloadProgress(0);
    cancelDownload.current = source.cancel;
    const { id: fileSourceId } = fileSources.find((source: FileSource) => source.slug === file.origin_slug);

    axios
      .post(`/vfs/file_sources/${fileSourceId}/download/`, {
        key: file.key
      })
      .then(async (fileSourcesDownloadResponse) => {
        const url = fileSourcesDownloadResponse.data?.data?.url;

        await baseAxios({
          url,
          method: 'GET',
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setDownloadProgress(percentCompleted);
            }
          },
          cancelToken: source.token
        })
          .then((signedDownloadResponse) => {
            const url = window.URL.createObjectURL(new Blob([signedDownloadResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          })
          .catch((thrown) => {
            setDownloadProgress(0);
            if (baseAxios.isCancel(thrown)) {
              console.log('Request canceled', thrown.message);
              info('Download canceled');
            } else if (thrown?.response?.status === 403) {
              error('Access denied: Unable to download file.');
            } else {
              error('Unable to download file.');
            }
          })
          .finally(() => setDownloadingFile(null));
      })
      .catch(() => setDownloadingFile(null));
  };

  const handleCancelDownload = (e: any) => {
    e.stopPropagation();
    if (cancelDownload.current) {
      cancelDownload.current();
    }
  };

  return (
    <StyledListItemButton
      onClick={!isArchived && onClick && canWriteFileManager ? onClick : () => {
        error('You do not have permission to perform this action.');
      }}
      hover={!isArchived ? hover : false}
      inFileQueue={inFileQueue}
      onMouseEnter={!isArchived ? mouseEnter : undefined}
      onMouseLeave={!isArchived ? mouseLeave : undefined}
      selected={selected}
      sx={{ cursor: !onClick ? 'default' : 'pointer', borderRadius: 0 }}
      isArchived={+isArchived}
    >
      {downloadingFile?.key === item.key && (
        <>
          <Tooltip title="Cancel download" placement="top">
            <Button onClick={handleCancelDownload} size="small" sx={{ right: 0, position: 'absolute', zIndex: 9 }}>
              <PolarisIcon icon="cancel" height={20} />
            </Button>
          </Tooltip>
          <StyledProgress value={downloadProgress} max="100" data-label={`Downloading ${downloadProgress}%`} />
        </>
      )}

      <Grid container wrap="nowrap" alignItems="center" style={{ columnGap: 8 }}>
        <Grid item style={{ width: fileColumns[0].width }}>
          <Grid container wrap="nowrap" alignItems="center">
            <StlyedListItemIcon className="styled-list-item-icon">
              {isFolder ? (
                <FolderIcon hover={hover} />
              ) : (
                <FileSelectedIcon hover={!onClick ? false : hover} selected={!onClick ? false : selected || inFileQueue} />
              )}
            </StlyedListItemIcon>

            <Tooltip title={isFolder && !item.name.endsWith('/') ? `${fileName}/` : fileName}>
              <StyledFileItemText name={isFolder && !item.name.endsWith('/') ? `${fileName}/` : fileName} isMidAction={isMidAction} />
            </Tooltip>
            {isArchived && (
              <div style={{ marginLeft: 10, height: 18 }}>
                <Tooltip title={'Archived'} arrow>
                  <PolarisIcon icon="archive" height={18} />
                </Tooltip>
              </div>
            )}
          </Grid>
        </Grid>
        <Grid item style={{ width: fileColumns[1].width }}>
          {isFolder ? (
            ''
          ) : (
            <StyledFileItemTextFileSize
              primary={item.last_modified ? formatDate(new Date(item.last_modified).toISOString()) : '-'}
              disableTypography
            />
          )}
        </Grid>
        <Grid item style={{ width: fileColumns[2].width }}>
          {!isFolder && <StyledFileItemTextFileSize primary={isMidAction ? '' : item.size ? formatBytes(item.size) : '-'} disableTypography />}
        </Grid>
        <Grid item style={{ width: fileColumns[3].width }}>
          {!isArchived && <FileActions file={item} isMidAction={isMidAction} handleDownload={handleDownload} />}
        </Grid>
        <IconsWrapperDiv>
          {isMidAction && <StyledRefreshFiles className="styled-refresh-files" />}
          {onRemove && <RemoveButton onClick={onRemove} />}
        </IconsWrapperDiv>
      </Grid>
    </StyledListItemButton>
  );
};
export default FileItem;

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => !['hover', 'inFileQueue', 'isArchived'].includes(prop)
})<{ inFileQueue: boolean; hover: boolean; isArchived: number }>`
  width: auto;

  ${({ isArchived }) => (isArchived ? `opacity: 0.5; cursor: default` : '')};

  ${(props) =>
    props.hover
      ? `
    background-color: ${props.theme.palette.background.default};
  `
      : null};

  ${(props) =>
    props.inFileQueue
      ? `
    background: ${props.theme.palette.primary.main + '2a'};
    &:hover {
      background: ${props.theme.palette.primary.main + '2a'};
    }
    .styled-list-item-icon {
      opacity: 1;
    }
  `
      : null};
`;

const StlyedListItemIcon = styled(ListItemIcon)`
  min-width: 1rem;
  opacity: ${(props) => (props.theme.palette.mode === 'dark' ? 0.3 : 0.6)};
  margin-right: ${(props) => props.theme.spacing(1.5)};
  display: inline-block;
`;

const StyledRefreshFiles = styled(RefreshFiles)`
  border: 0;
  margin: 0;
`;

const StyledFileItemText = styled(FileItemText, {
  shouldForwardProp: (prop) => !['isMidAction'].includes(prop)
})<{ isMidAction: boolean }>`
  ${(props) =>
    props.isMidAction
      ? `
    opacity: 0.5;
  `
      : null};
`;

const StyledFileItemTextFileSize = styled(ListItemText)`
  white-space: nowrap;
  opacity: 0.6;
  min-width: 100px;
  text-align: right;
`;

const StyledProgress = styled.progress`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 0;
  pointer-events: none;
  opacity: 0.8;
  z-index: 1;
  &::-webkit-progress-bar {
    background-color: ${({ theme }) => theme.palette.background.default};
  }
  &::-webkit-progress-value {
    background-color: ${({ theme }) => theme.palette.primary.main};
  }
  &::before {
    content: attr(data-label);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 1.2rem;
    font-weight: 700;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const IconsWrapperDiv = styled.div`
  display: flex;
  margin: ${(props) => props.theme.spacing(-1)};
  margin-left: auto;

  & .MuiIconButton-sizeSmall {
    opacity: 0.5;
    padding-right: ${(props) => props.theme.spacing(0.5)};
    width: 35px;
    height: 35px;
    &:hover {
      opacity: 1;
      color: ${(props) => props.theme.palette.primary.light};
    }
  }

  & .styled-refresh-files {
    opacity: 0.8;
    padding: 0;
  }
`;
